import { clockDateTime, clockTime, formatDateKey, shiftDateKey, trailLabel } from '../api.ts';

export type TimeMode = 'hours' | 'date';

interface Props {
  mode: TimeMode;
  onModeChange: (m: TimeMode) => void;
  /** True only in hours mode with auto-refresh on. */
  live: boolean;
  onLive: () => void;

  windowEnd: number;
  sliderMin: number;
  sliderMax: number;
  onScrub: (t: number) => void;

  trailHours: number;
  trailOptions: readonly number[];
  onTrailChange: (h: number) => void;

  dateKey: string;
  dateMin: string;
  dateMax: string;
  onDateChange: (key: string) => void;

  reportCount: number;
}

/**
 * Two ways to choose what the map shows: a rolling window of recent hours (with
 * an optional "Live" mode that follows the newest report), or one calendar day.
 * Either way the slider scrubs through time, and everything already loaded is
 * filtered locally, so moving it never waits on the network.
 */
export function TimeControls(p: Props) {
  const date = p.mode === 'date';
  const isToday = p.dateKey === p.dateMax;

  return (
    <div className="time-bar">
      <div className="row time-row">
        {/* Grouped so the toggle always shares a line with the live button and
            date/time label (mobile: see .time-row-top's doc comment in
            accounts.css) rather than wrapping away from them onto its own
            line depending on how long the label/sub text happens to be. */}
        <div className="time-row-top">
          <button className="live-btn" data-live={String(p.live)} onClick={p.onLive} aria-pressed={p.live}>
            {p.live ? '● Live' : 'Go Live'}
          </button>

          <span className="label" data-date={String(date)}>{date ? formatDateKey(p.dateKey) : clockDateTime(p.windowEnd)}</span>
          <span className="sub">
            {/* The trail length is already shown by the selected chip below, so
                hours mode has nothing to add here beyond the report count. */}
            {date && (p.windowEnd >= p.sliderMax ? 'all day · ' : `up to ${clockTime(p.windowEnd)} · `)}
            {p.reportCount} report{p.reportCount === 1 ? '' : 's'}
          </span>

          <span className="spacer" />

          <div className="seg seg-small" role="tablist" aria-label="Time selection">
            <button role="tab" aria-selected={!date} className="seg-btn" onClick={() => p.onModeChange('hours')}>
              Hours
            </button>
            <button role="tab" aria-selected={date} className="seg-btn" onClick={() => p.onModeChange('date')}>
              Date
            </button>
          </div>
        </div>

        {date ? (
          <div className="date-controls">
            <button
              className="chip icon-chip"
              onClick={() => p.onDateChange(shiftDateKey(p.dateKey, -1))}
              disabled={p.dateKey <= p.dateMin}
              aria-label="Previous day"
            >
              ‹
            </button>
            <input
              type="date"
              className="date-input"
              value={p.dateKey}
              min={p.dateMin}
              max={p.dateMax}
              onChange={(e) => e.target.value && p.onDateChange(e.target.value)}
              aria-label="Choose a date"
            />
            <button
              className="chip icon-chip"
              onClick={() => p.onDateChange(shiftDateKey(p.dateKey, 1))}
              disabled={isToday}
              aria-label="Next day"
            >
              ›
            </button>
          </div>
        ) : (
          <div className="chip-row" role="group" aria-label="Trail length">
            {p.trailOptions.map((h) => (
              <button key={h} className="chip" aria-pressed={p.trailHours === h} onClick={() => p.onTrailChange(h)}>
                {trailLabel(h)}
              </button>
            ))}
          </div>
        )}
      </div>

      <input
        type="range"
        min={p.sliderMin}
        max={p.sliderMax}
        step={60_000}
        value={Math.min(Math.max(p.windowEnd, p.sliderMin), p.sliderMax)}
        aria-label={date ? 'Scrub through the day' : 'Scrub through time'}
        onChange={(e) => p.onScrub(Number(e.target.value))}
      />
      <div className="ticks">
        <span>{date ? clockTime(p.sliderMin) : clockDateTime(p.sliderMin)}</span>
        <span>{date && !isToday ? 'end of day' : 'now'}</span>
      </div>
    </div>
  );
}
